import React, { Suspense, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { selectHydrationFinished } from 'app/hooks/reduxCreateSelectorHooks';
import { checkPlayAd } from 'app/helpers/adStepper';
import useVideoStartTracking from 'app/components/tv/trackVideoSeeking';
import { LOADING_MESSAGE, LoadingIndicator } from '@fupa/fupa-uikit';
import { updateCurrentVideo } from 'app/routes/tv/TvPlaylistPageActions';
import { trackVideoProgress } from 'app/components/tv/helpers/trackVideoProgress';
import { LoadingOverlay } from 'app/components/tv/LoadingOverlay';
import { captureSentryException } from 'app/services/sentryLogging';
import { useWatchedPlaylistTracking } from 'app/hooks/useWatchedPlaylistTracking';
const VideoJS = /*#__PURE__*/React.lazy(() => import('app/components/tv/VideoJS'));

// scrollable wrapper necessary to hide address-bar in mobile Browsers
// for chrome ios it's not enough to set 101vh to hide address-bar
// -> therefore it's set to 200vh to hopefully support all browsers
const Wrapper = styled.div.withConfig({
  componentId: "sc-yyvp9x-0"
})(["position:relative;width:100vw;height:200vh;background-color:black;padding-top:env(safe-area-inset-top);"]);

// Player-Container is fixed with height 100%
// to always have the height of the visible screen on mobile browsers,
// no matter if address-bar is shown or not
const VideoPlayerContainer = styled.div.withConfig({
  componentId: "sc-yyvp9x-1"
})(["position:fixed;top:0;left:0;width:100%;height:100%;overflow:hidden;"]);
export default function VideoPlayer(_ref) {
  let {
    video,
    playlist,
    isFetching,
    dispatch,
    ssrLoaded
  } = _ref;
  const currentVideoRef = useRef(null);
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const history = useHistory();
  const {
    matchPlaylistSlug
  } = useParams();
  const hydrationFinished = useSelector(selectHydrationFinished);
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && currentVideoRef.current) {
        // update credits when site is left before video ended
        trackVideoProgress(currentVideoRef.current, player.played());
      }
    };
  }, [playerReady]);
  useEffect(() => {
    if (!video) {
      return;
    }
    currentVideoRef.current = video;
  }, [video]);
  useVideoStartTracking(playerRef, video, playerReady, playlist);
  useWatchedPlaylistTracking(playlist, video, playerRef, playerReady);
  const setCurrentVideo = React.useCallback(newCurrentVideo => {
    dispatch(updateCurrentVideo(newCurrentVideo));
    history.replace("/tv/match/".concat(matchPlaylistSlug, "/").concat(newCurrentVideo.slug));
  }, [matchPlaylistSlug, history, dispatch]);
  if (!video || !playlist) {
    if (isFetching) {
      return /*#__PURE__*/React.createElement(LoadingIndicator, {
        message: LOADING_MESSAGE.loadingData,
        bgColor: false
      });
    } else {
      return null;
    }
  }
  const handlePlayerReady = player => {
    playerRef.current = player;
    setPlayerReady(true);
    player.on('ended', () => {
      if (currentVideoRef.current) {
        trackVideoProgress(currentVideoRef.current, player.played());
      }
    });
    player.on('error', () => {
      const error = player.error();
      if (!error) {
        return;
      }
      if (error.code === error.MEDIA_ERR_NETWORK) {
        setIsNetworkError(true);
      }
      logVjsError(error, player.currentSrc());
      player.play();
    });
  };
  const Player = hydrationFinished ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(LoadingOverlay, {
      show: true
    })
  }, /*#__PURE__*/React.createElement(VideoJS, {
    onReady: handlePlayerReady,
    video: video,
    playlist: playlist,
    setCurrentVideo: setCurrentVideo,
    playerReady: playerReady,
    isNetworkError: isNetworkError,
    setIsNetworkError: setIsNetworkError,
    isFirstPage: ssrLoaded
  })) : /*#__PURE__*/React.createElement(LoadingOverlay, {
    show: true
  });
  return /*#__PURE__*/React.createElement(Wrapper, {
    "data-sentry-element": "Wrapper",
    "data-sentry-component": "VideoPlayer",
    "data-sentry-source-file": "VideoPlayer.tsx"
  }, /*#__PURE__*/React.createElement(VideoPlayerContainer, {
    "data-sentry-element": "VideoPlayerContainer",
    "data-sentry-source-file": "VideoPlayer.tsx"
  }, Player));
}
const logVjsError = (error, src) => {
  if (error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    captureSentryException(new Error('MediaError: Source is not supported'), {
      extra: {
        type: 'vjs error',
        errorInfo: error,
        src,
        playAd: checkPlayAd()
      }
    });
  } else {
    captureSentryException(new Error("VideoJS error-handler: ".concat(error.message)), {
      extra: {
        errorInfo: {
          type: 'vjs error',
          code: error.code,
          message: error.message
        },
        src
      }
    });
  }
};